import { useState, useMemo } from "react";
import { Plus, Edit2, Trash2, Copy, FileText, Zap, ChevronLeft, ChevronRight, Eye, EyeOff, Code, Blocks, Code2, Sparkles, Wand2, CheckCircle2, AlertTriangle, Loader2, Hammer, XCircle, RefreshCw, Flag, Layers } from "lucide-react";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { BulkActionBar } from "./BulkActionBar";
import { describeInputs } from "./BlocksInputsEditor";
import { solutionStatus } from "../blocks/solutionTools";
import { MODES, MODE_META, modeLabel, normalizeMode } from "../../lib/modes";

const SolutionBadge = ({ question }) => {
  const st = solutionStatus(question);
  if (st.state === "verified") {
    return (
      <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-400 bg-emerald-500/10" title={st.source === "auto" ? "Auto-converted from the Python solution and verified" : "Built on the site and verified"} data-testid={`solution-status-${question.question_id}`}>
        <CheckCircle2 className="w-3 h-3" /> Blocks {st.source === "auto" ? "(auto)" : "(manual)"}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-amber-600 border-amber-400 bg-amber-500/10" title={st.note || "No block solution yet"} data-testid={`solution-status-${question.question_id}`}>
      <AlertTriangle className="w-3 h-3" /> Python only
    </Badge>
  );
};

// Last sample-solution check result -> {tone, label, title, flagged}
export const checkSummary = (question) => {
  const c = question.solution_check;
  if (!c) return { tone: "muted", label: "Not checked", title: "Run Check Sample Solutions (Tools) or re-check this question", flagged: false };
  const when = c.checked_at ? new Date(c.checked_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "";
  if (c.status === "fail") return { tone: "red", label: `Fails ${c.passed}/${c.total}`, title: `Sample solution fails ${c.total - c.passed} test(s) · ${when}`, flagged: true };
  if (c.blocks_status === "fail") return { tone: "red", label: "Blocks fails", title: `Block solution no longer passes the tests · ${when}`, flagged: true };
  if (c.status === "no_tests") return { tone: "amber", label: "No tests", title: `No test cases or example output - can't be graded · ${when}`, flagged: true };
  if (c.status === "no_solution") return { tone: "amber", label: "No solution", title: `No sample solution to check · ${when}`, flagged: true };
  if (c.python_status === "fail") return { tone: "green", label: `Passes ${c.passed}/${c.total}`, title: `Block solution passes every test (students see the blocks). Note: the Python sample fails some tests · ${when}`, flagged: false, note: "py" };
  return { tone: "green", label: `Passes ${c.passed}/${c.total}`, title: `Sample solution passes every test · ${when}`, flagged: false };
};

const CHECK_TONES = {
  muted: "text-muted-foreground border-border",
  red: "text-red-600 border-red-400 bg-red-500/10",
  amber: "text-amber-600 border-amber-400 bg-amber-500/10",
  green: "text-emerald-600 border-emerald-400 bg-emerald-500/10",
};

const CheckBadge = ({ question, onRecheck, rechecking }) => {
  const st = checkSummary(question);
  const Icon = st.tone === "green" ? CheckCircle2 : st.tone === "red" ? XCircle : AlertTriangle;
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="outline" className={`gap-1 whitespace-nowrap ${CHECK_TONES[st.tone]}`} title={st.title} data-testid={`solution-check-${question.question_id}`}>
        {st.tone !== "muted" && <Icon className="w-3 h-3" />} {st.label}
        {st.note && <span className="text-[10px] text-amber-600 font-normal" data-testid={`solution-check-python-note-${question.question_id}`}>· py sample fails</span>}
      </Badge>
      {onRecheck && (
        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={rechecking} onClick={() => onRecheck(question)} title="Re-check this sample solution against its tests" data-testid={`recheck-solution-${question.question_id}`}>
          {rechecking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      )}
    </span>
  );
};

const ITEMS_PER_PAGE = 50;
const TRACK_KEY = "admin_questions_track";

// Helper to count lines in sample solution
const countSolutionLines = (sampleSolution) => {
  if (!sampleSolution || typeof sampleSolution !== 'string') return 0;
  return sampleSolution.split('\n').filter(line => line.trim().length > 0).length;
};

// Helper to format MM-DD date as readable string
const formatAnnualDate = (dateStr) => {
  if (!dateStr) return "—";
  const parts = dateStr.split("-");
  if (parts.length !== 2) return dateStr;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthIndex = parseInt(parts[0], 10) - 1;
  const day = parseInt(parts[1], 10);
  return monthIndex >= 0 && monthIndex < 12 ? `${months[monthIndex]} ${day}` : dateStr;
};

function PaginationControls({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div className="flex items-center justify-between mt-4 px-2">
      <p className="text-sm text-muted-foreground">Showing {startItem}-{endItem} of {totalItems}</p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="gap-1" data-testid="pagination-prev">
          <ChevronLeft className="w-4 h-4" /> Previous
        </Button>
        <span className="text-sm text-muted-foreground px-2">Page {currentPage} of {totalPages}</span>
        <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="gap-1" data-testid="pagination-next">
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// One pool (Warm Up / Challenge) table for the active track
function PoolTable({ pool, questions, track, headerActions, onEditQuestion, onDeleteQuestion, onDuplicateQuestion, onMakeBlocksVersion, onConvertSolution, onBuildSolution, onGenerateVariations, converting, onRecheckSolution, rechecking, canBulk, onBulkAction }) {
  const [page, setPage] = useState(1);
  const [hideScheduled, setHideScheduled] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const isDaily = pool === "daily";
  const isBlocks = track === "blocks" || track === "junior";
  const isJunior = track === "junior";

  const scheduledCount = useMemo(() => questions.filter((q) => q.date).length, [questions]);
  const filtered = useMemo(() => (hideScheduled ? questions.filter((q) => !q.date) : questions), [questions, hideScheduled]);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Selection only ever holds questions still in this table (they may have been moved / deleted elsewhere)
  const ids = useMemo(() => new Set(questions.map((q) => q.question_id)), [questions]);
  const selected = useMemo(() => new Set([...picked].filter((id) => ids.has(id))), [picked, ids]);
  const pageIds = paginated.map((q) => q.question_id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPage = pageIds.some((id) => selected.has(id));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setPicked((prev) => { const n = new Set(prev); allOnPage ? pageIds.forEach((id) => n.delete(id)) : pageIds.forEach((id) => n.add(id)); return n; });
  const runBulk = async (action, targetPool) => {
    setBulkBusy(true);
    try {
      const ok = await onBulkAction(action, [...selected], targetPool);
      if (ok) setPicked(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-6" data-testid={`${track}-${pool}-pool`}>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDaily ? "bg-primary/10" : "bg-secondary/10"}`}>
            {isDaily ? <FileText className="w-5 h-5 text-primary" /> : <Zap className="w-5 h-5 text-secondary" />}
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg">{isDaily ? "Warm Up Pool" : "Challenge Pool"}</h2>
            <p className="text-sm text-muted-foreground">
              {filtered.length} {modeLabel(track)} questions
              {hideScheduled && scheduledCount > 0 && <span className="text-muted-foreground/70"> ({scheduledCount} scheduled hidden)</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {scheduledCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => { setHideScheduled(!hideScheduled); setPage(1); }} className="gap-2" data-testid={`toggle-scheduled-${pool}-btn`}>
              {hideScheduled ? <><Eye className="w-4 h-4" /> Show Scheduled</> : <><EyeOff className="w-4 h-4" /> Hide Scheduled</>}
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{scheduledCount}</Badge>
            </Button>
          )}
          {headerActions}
        </div>
      </div>

      {canBulk && selected.size > 0 && (
        <BulkActionBar pool={pool} count={selected.size} ids={[...selected]} onAction={runBulk} onClear={() => setPicked(new Set())} busy={bulkBusy} />
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {canBulk && (
                <TableHead className="w-10">
                  <Checkbox checked={allOnPage ? true : someOnPage ? "indeterminate" : false} onCheckedChange={togglePage} disabled={pageIds.length === 0} aria-label="Select all questions on this page" data-testid={`select-all-${pool}`} />
                </TableHead>
              )}
              <TableHead>Title</TableHead>
              <TableHead>Pool</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>{isJunior ? "Grid" : "Test Cases"}</TableHead>
              <TableHead>{isJunior ? "Block limit" : isBlocks ? "Inputs" : "Solution Lines"}</TableHead>
              {isBlocks && <TableHead>Solution</TableHead>}
              <TableHead>Check</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.map((question) => (
              <TableRow key={question.question_id} data-state={selected.has(question.question_id) ? "selected" : undefined}>
                {canBulk && (
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(question.question_id)} onCheckedChange={() => toggle(question.question_id)} aria-label={`Select ${question.title}`} data-testid={`select-question-${question.question_id}`} />
                  </TableCell>
                )}
                <TableCell className="font-medium">{question.title}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={isDaily ? "text-primary border-primary" : "text-secondary border-secondary"}>
                    {isDaily ? "Daily" : "Challenge"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatAnnualDate(question.date)}</TableCell>
                <TableCell>{isJunior ? <span className="text-xs text-muted-foreground" data-testid={`nav-grid-summary-${question.question_id}`}>{(question.blocks?.nav?.rocks || []).length} rocks · {(question.blocks?.nav?.dangers || []).length} danger · {(question.blocks?.nav?.tunnels || []).length} tunnels</span> : (question.test_cases?.length || 0)}</TableCell>
                <TableCell>
                  {isBlocks ? (
                    <span className="text-xs font-mono text-muted-foreground" data-testid={`blocks-inputs-${question.question_id}`}>{isJunior ? (question.blocks?.nav?.max_blocks ? `max ${question.blocks.nav.max_blocks} blocks` : "unlimited") : describeInputs(question.blocks?.inputs)}</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 ${countSolutionLines(question.sample_solution || question.starter_code) === 0 ? 'text-muted-foreground' : ''}`}>
                      <Code className="w-3 h-3" />
                      {countSolutionLines(question.sample_solution || question.starter_code) || '—'}
                    </span>
                  )}
                </TableCell>
                {isBlocks && (
                  <TableCell><SolutionBadge question={question} /></TableCell>
                )}
                <TableCell><CheckBadge question={question} onRecheck={onRecheckSolution} rechecking={rechecking.has(question.question_id)} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {isBlocks && onBuildSolution && (
                    <Button variant="ghost" size="icon" onClick={() => onBuildSolution(question)} title="Build the sample solution with blocks" data-testid={`build-solution-${question.question_id}`}>
                      <Hammer className="w-4 h-4 text-amber-500" />
                    </Button>
                  )}
                  {isBlocks && !isJunior && onConvertSolution && (
                    <Button variant="ghost" size="icon" disabled={converting.has(question.question_id)} onClick={() => onConvertSolution(question)} title="Convert the Python solution to blocks (verified against the tests)" data-testid={`convert-solution-${question.question_id}`}>
                      {converting.has(question.question_id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 text-sky-500" />}
                    </Button>
                  )}
                  {!isBlocks && onMakeBlocksVersion && (
                    <Button variant="ghost" size="icon" onClick={() => onMakeBlocksVersion(question)} title="Make Blocks version" data-testid={`make-blocks-${question.question_id}`}>
                      <Blocks className="w-4 h-4 text-sky-500" />
                    </Button>
                  )}
                  {onGenerateVariations && (
                    <Button variant="ghost" size="icon" onClick={() => onGenerateVariations(question)} title="Generate 5 similar problems (same pool, same difficulty, titled _a … _e)" data-testid={`generate-variations-${question.question_id}`}>
                      <Layers className="w-4 h-4 text-primary" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => onDuplicateQuestion(question)} title="Duplicate question" data-testid={`duplicate-question-${question.question_id}`}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onEditQuestion(question)} data-testid={`edit-question-${question.question_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDeleteQuestion(question.question_id)} data-testid={`delete-question-${question.question_id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={(isBlocks ? 8 : 7) + (canBulk ? 1 : 0)} className="text-center text-muted-foreground py-8">
                  {hideScheduled && scheduledCount > 0
                    ? `All ${isDaily ? "daily" : "challenge"} questions are scheduled. Click 'Show Scheduled' to view them.`
                    : isJunior
                      ? `No Junior Blocks ${isDaily ? "warm up" : "challenge"} questions yet - click "Add Junior Blocks Question" to draw a navigation grid.`
                    : isBlocks
                      ? `No Blocks ${isDaily ? "warm up" : "challenge"} questions yet - use "Make Blocks version" on a Python question or the generate tool.`
                      : `No ${isDaily ? "daily" : "challenge"} questions yet`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls currentPage={page} totalPages={totalPages} onPageChange={setPage} totalItems={filtered.length} itemsPerPage={ITEMS_PER_PAGE} />
    </div>
  );
}

export const readQuestionsTrack = () => normalizeMode(localStorage.getItem(TRACK_KEY));

export function QuestionsTab({
  questions,
  isMasterAdmin,
  track,
  onTrackChange,
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onDuplicateQuestion,
  onMakeBlocksVersion,
  onGenerateBlocks,
  onConvertSolution,
  onConvertMissing,
  onBuildSolution,
  onGenerateVariations,
  converting = new Set(),
  onRecheckSolution,
  rechecking = new Set(),
  onBulkAction,
}) {
  const setTrack = (t) => { localStorage.setItem(TRACK_KEY, t); onTrackChange(t); };
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const missingSolutions = useMemo(() => questions.filter((q) => q.mode === "blocks" && solutionStatus(q).state !== "verified"), [questions]);

  const counts = useMemo(() => ({
    python: questions.filter((q) => normalizeMode(q.mode) === "python").length,
    blocks: questions.filter((q) => q.mode === "blocks").length,
    junior: questions.filter((q) => q.mode === "junior").length,
  }), [questions]);
  const inTrack = useMemo(() => questions.filter((q) => normalizeMode(q.mode) === track), [questions, track]);
  const flaggedCount = useMemo(() => inTrack.filter((q) => checkSummary(q).flagged).length, [inTrack]);
  const visible = flaggedOnly ? inTrack.filter((q) => checkSummary(q).flagged) : inTrack;
  const dailyQuestions = visible.filter((q) => q.pool === "daily" || q.pool === "warmup");
  const challengeQuestions = visible.filter((q) => q.pool === "challenge");

  const tableProps = { track, onEditQuestion, onDeleteQuestion, onDuplicateQuestion, onMakeBlocksVersion, onConvertSolution, onBuildSolution, onGenerateVariations, converting, onRecheckSolution, rechecking, canBulk: !!(isMasterAdmin && onBulkAction), onBulkAction };

  return (
    <div className="space-y-6">
      {/* Track sub-tabs: Junior Blocks | Blocks | Python questions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center rounded-xl border border-border bg-muted/40 p-1" role="tablist" data-testid="questions-track-tabs">
          {MODES.map((v) => ({ v, label: MODE_META[v].label, Icon: MODE_META[v].icon })).map(({ v, label, Icon }) => (
            <button
              key={v}
              role="tab"
              aria-selected={track === v}
              onClick={() => setTrack(v)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${track === v ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`questions-track-${v}`}
            >
              <Icon className="w-4 h-4" /> {label} questions
              <Badge variant="secondary" className="px-1.5 py-0 text-xs">{counts[v]}</Badge>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {(flaggedCount > 0 || flaggedOnly) && (
            <Button variant={flaggedOnly ? "default" : "outline"} onClick={() => setFlaggedOnly(!flaggedOnly)} className={`gap-2 rounded-xl ${flaggedOnly ? "" : "border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"}`} title="Questions whose sample solution failed the last check, or that have no solution / test data" data-testid="flagged-only-toggle">
              <Flag className="w-4 h-4" /> {flaggedOnly ? "Show all questions" : `Show ${flaggedCount} flagged`}
            </Button>
          )}
          {track === "blocks" && onConvertMissing && missingSolutions.length > 0 && (
            <Button variant="outline" onClick={() => onConvertMissing(missingSolutions)} disabled={converting.size > 0} className="gap-2 rounded-xl" data-testid="convert-missing-solutions-btn">
              {converting.size > 0 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Convert {missingSolutions.length} missing block solution{missingSolutions.length === 1 ? "" : "s"}
            </Button>
          )}
          {track === "blocks" && isMasterAdmin && onGenerateBlocks && (
            <Button variant="outline" onClick={onGenerateBlocks} className="gap-2 rounded-xl border-sky-400 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40" data-testid="generate-blocks-open-btn">
              <Sparkles className="w-4 h-4" /> Generate from Python questions
            </Button>
          )}
        </div>
      </div>

      <PoolTable
        pool="daily"
        questions={dailyQuestions}
        {...tableProps}
        headerActions={
          <Button onClick={() => onAddQuestion(track)} className="gap-2 rounded-xl" data-testid="add-question-btn">
            <Plus className="w-4 h-4" /> Add {track === "python" ? "" : `${modeLabel(track)} `}Question
          </Button>
        }
      />
      <PoolTable pool="challenge" questions={challengeQuestions} {...tableProps} />
    </div>
  );
}
